import { MARGIN_EXCEPTION_ROLES } from '../../constant/financial.authorization.constant';

export const EXCEPTION_ROLES = MARGIN_EXCEPTION_ROLES;

const REPLACES_TOTAL_OPERATION = 'equal';
const BASE_PRICE_KEY = 'base_price';

const CURRENT_SERVICE_PRICE = 'current_service_price';
const SERVICE_BASE_PRICE = 'service_base_price';
const CHARGE_PACKAGE = 'charge_package';
const SUBTRACT = 'subtract';

const sameIdentifier = (left, right) => String(left) === String(right);

const matchesOption = (option, entry) =>
    sameIdentifier(option.serviceId, entry.serviceId)
    && (option.calculationServiceId || null) === (entry.calculationServiceId || null);

export const findPieceOption = (pieceOptions, entry) =>
    (pieceOptions || []).find(option => matchesOption(option, entry));

export const defaultPieceOption = (pieceOptions) =>
    (pieceOptions || []).find(option => option.isRequestService) || (pieceOptions || [])[0] || null;

const CURRENCY_LOCALE = 'pt-BR';
const CURRENCY_OPTIONS = { style: 'currency', currency: 'BRL' };
const MINUS_SIGN = '−';

const asCurrency = (value) => Number(value || 0).toLocaleString(CURRENCY_LOCALE, CURRENCY_OPTIONS);

const withSign = (value) => [value < 0 ? MINUS_SIGN : '+', asCurrency(Math.abs(value))].join('');

const sidesOf = (change) => [
    change.customerDelta ? [withSign(change.customerDelta), 'cliente'].join(' ') : null,
    change.faciliterDelta ? [withSign(change.faciliterDelta), 'faciliter'].join(' ') : null
].filter(Boolean).join(', ');

const describeChange = (change) => {
    const head = [change.label, change.pieceLabel].filter(Boolean).join(': ');
    const sides = sidesOf(change);

    return sides ? `${head} (${sides})` : head;
}

export const describeSelection = (changes) => (changes || []).map(describeChange).join('; ');

const faciliterAmountOf = (item, option, runningAmount) => {
    if (item.faciliterAmountSource === CURRENT_SERVICE_PRICE) return runningAmount;
    if (item.faciliterAmountSource === SERVICE_BASE_PRICE && option) return Number(option.faciliterBasePrice || 0);
    return Number(item.faciliterAmount || 0);
}

const customerDeltaOf = (item, option) => {
    if (item.customerSide !== CHARGE_PACKAGE) return Number(item.customerAmount || 0);
    const credits = Number(item.customerCreditAmount || 0);
    return option ? credits * Number(option.customerPrice || 0) : Number(item.customerAmount || 0);
}

const changeFor = (item, entry, pieceOptions, runningAmount) => {
    const option = entry.serviceId ? findPieceOption(pieceOptions, entry) : null;
    const amount = faciliterAmountOf(item, option, runningAmount);
    const signed = item.faciliterOperation === SUBTRACT ? -Math.abs(amount) : Math.abs(amount);

    return {
        catalogItemId: item.id,
        key: item.key,
        label: item.label,
        pieceLabel: option ? [option.serviceName, option.calculationServiceName].filter(Boolean).join(' · ') : null,
        customerDelta: customerDeltaOf(item, option),
        faciliterDelta: signed,
        replacesTotal: item.faciliterAmountSource === CURRENT_SERVICE_PRICE
    };
}

const collectChanges = (catalog, selection, pieceOptions, startingAmount) => {
    const changes = [];
    let runningAmount = startingAmount;

    (selection || []).forEach(entry => {
        const item = (catalog || []).find(candidate => sameIdentifier(candidate.id, entry.catalogItemId));
        if (!item || !item.available) return;

        const change = changeFor(item, entry, pieceOptions, runningAmount);
        changes.push(change);
        runningAmount += change.faciliterDelta;
    });

    return { changes, faciliterNext: runningAmount };
}

const replacesTotalOf = (fee) => fee.operationType === REPLACES_TOTAL_OPERATION && fee.key !== BASE_PRICE_KEY;

const replacementIn = (priceFees) => (priceFees || []).find(replacesTotalOf) || null;

const totalsOf = (changes, faciliterNow, faciliterNext, customerNow) => {
    const customerDelta = changes.reduce((total, change) => total + change.customerDelta, 0);
    const customerNext = customerNow + customerDelta;

    return {
        customerNow, customerNext, customerDelta,
        faciliterNow, faciliterNext, faciliterDelta: faciliterNext - faciliterNow,
        companyNow: customerNow - faciliterNow,
        companyNext: customerNext - faciliterNext
    };
}

const marginVerdict = (changes, totals, role) => {
    const invertsMargin = changes.length > 0 && totals.companyNext < 0;

    return {
        invertsMargin,
        blocked: invertsMargin && !EXCEPTION_ROLES.includes(role),
        requiresReason: invertsMargin
    };
}

export function buildPreview({ catalog, pieceOptions, selection, faciliterAmount, customerAmount, role, priceFees }) {

    const faciliterNow = Number(faciliterAmount || 0);
    const { changes, faciliterNext } = collectChanges(catalog, selection, pieceOptions, faciliterNow);
    const totals = totalsOf(changes, faciliterNow, faciliterNext, Number(customerAmount || 0));
    const replacement = replacementIn(priceFees);

    return {
        changes,
        hasChanges: changes.length > 0,
        ...totals,
        ...marginVerdict(changes, totals, role),
        neutralizedByReplacement: Boolean(replacement) && changes.length > 0,
        replacementLabel: replacement ? replacement.label : null
    };
}
